import './DiscoverHomes.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { Helmet } from "react-helmet";


const DiscoverHomes = () => {
  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();

  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    const fetchTopHomes = async () => {
      try {
        const houseQuery = query(
          collection(db, 'house-listings'),
          orderBy('views', 'desc'),
          limit(20)
        );
        const rentQuery = query(
          collection(db, 'rent-listings'),
          orderBy('views', 'desc'),
          limit(20)
        );

        const houseSnap = await getDocs(houseQuery);
        const rentSnap = await getDocs(rentQuery);

        const combined = [
          ...houseSnap.docs.map(d => ({ id: d.id, ...d.data(), type: 'house' })),
          ...rentSnap.docs.map(d => ({ id: d.id, ...d.data(), type: 'rent' }))
        ];

        const listingsWithUrls = await Promise.all(
          combined.map(async (listing) => {
            let thumbnailPath = listing.thumbnailPath;

            if (!thumbnailPath && listing.images?.[0]) {
              thumbnailPath = `${listing.type === 'house' ? 'house-listings' : 'rent-listings'}/${listing.id}/${listing.images[0]}`;
            }

            let imageUrl = null;
            if (thumbnailPath) {
              const cached = sessionStorage.getItem(thumbnailPath);
              if (cached) {
                imageUrl = cached;
              } else {
                try {
                  imageUrl = await getDownloadURL(ref(storage, thumbnailPath));
                  sessionStorage.setItem(thumbnailPath, imageUrl);
                } catch (err) {
                  console.error('Thumbnail fetch failed:', listing.id);
                }
              }
            }

            return {
              ...listing,
              imageUrl,
              title: listing.pgName || listing.title || listing.name || 'Untitled Property',
              occupancy: listing.occupancy || 'Unknown occupancy',
              roomType: listing.roomType || 'Unknown room type',
              views: listing.views || 0,
            };
          })
        );

        setHouses(listingsWithUrls);
      } catch (error) {
        console.error('Error fetching homes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopHomes();
  }, [db, storage]);

  const handleCardClick = (houseId, type) => {
    if (!auth.currentUser) {
      alert('Please sign in to view this property.');
      return;
    }
    navigate(`/${type === 'house' ? 'pg' : 'rent'}/${houseId}`);
  };
 const handleShowMore = () => {
    setVisibleCount(prev => prev + 6); // Load 6 more
  };

  const visibleHouses = houses.slice(0, visibleCount);
  return (
    <div className="discover-container">

      {/* SEO */}
      <Helmet>
        <title>Discover PG, Rent & Hostels Near You | ApkaGhar</title>
      </Helmet>

      <div className="back-btn-wrapper">
        <button className="back-btn1" onClick={() => navigate(-1)}>← Back</button>
      </div>

      <h1 className="discover-title">Discover Your Next Ghar</h1>
      <p className="discover-subtitle">Explore homes that match your vibe — verified, nearby, and ready.</p>

      {/* ---------------- STATS CARDS ---------------- */}
      <div className="stats-cards">
        <div className="stats-card">
          <h2>500+</h2>
          <p>PGs & Rentals</p>
        </div>
        <div className="stats-card">
          <h2>98%</h2>
          <p>Verified Properties</p>
        </div>
        <div className="stats-card">
          <h2>₹5k–15k</h2>
          <p>Avg Rent/Month</p>
        </div>
        <div className="stats-card">
          <h2>Single / Shared</h2>
          <p>Most Popular Room</p>
        </div>
      </div>

      {/* ---------------- MINI GUIDE ---------------- */}
      <div className="mini-guide">
        <h2>🏠 PG & Rent Tips</h2>
        <ul>
          <li>🔹 Verify owner before booking</li>
          <li>🔹 Check inclusions: food, utilities, amenities</li>
          <li>🔹 Visit property physically if possible</li>
        </ul>
      </div>

      {/* ---------------- PG vs Rent Comparison ---------------- */}
      <div className="comparison-table">
        <h2>PG vs Rent – Quick Comparison</h2>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>PG</th>
              <th>Rent</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Fully Furnished</td>
              <td>✅</td>
              <td>❌</td>
            </tr>
            <tr>
              <td>Flexible Lease</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Food Options Included</td>
              <td>✅</td>
              <td>❌</td>
            </tr>
            <tr>
              <td>Utilities Included</td>
              <td>✅</td>
              <td>❌</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ---------------- PROPERTIES GRID ---------------- */}
 <h2 className="grid-heading">Available Properties</h2>
      {loading ? (
        <div className="skeleton-grid">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="skeleton-card"></div>
          ))}
        </div>
      ) : (
        <div className="card-grid">
          {visibleHouses.length > 0 ? (
            visibleHouses.map((house) => (
              <div
                key={house.id}
                className="house-card"
                onClick={() => handleCardClick(house.id, house.type)}
              >
                <img
                  loading="lazy"
                  src={house.imageUrl || 'https://placehold.co/400x300?text=No+Image'}
                  alt={house.title}
                  className="house-image"
                />
                <h3>{house.title}</h3>
                <p><strong>Occupancy:</strong> {house.occupancy}</p>
                <p><strong>Room Type:</strong> {house.roomType}</p>
              </div>
            ))
          ) : (
            <p>No homes found.</p>
          )}
        </div>
      )}

      {/* Show More Button */}
      {visibleCount < houses.length && !loading && (
        <div className="explore-btn-wrapper">
          <button className="explore-button" onClick={handleShowMore}>
            Explore More Homes
          </button>
        </div>
      )}
    </div>
  );
};

export default DiscoverHomes;
